import { AlertCircle, CheckCircle2, GitBranch, Loader2, MessageSquare, MinusCircle, Sparkles, X } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';

import { ScenarioRunFlowDialog } from '@/components/eval-studio/ScenarioRunFlowDialog';
import { formatCompactDuration } from '@/components/eval-studio/scenarioStepFormatting';
import type { ScenarioRunDetail } from '@/components/eval-studio/evalStudioApi';
import { isTerminalScenarioRunStatus } from '@/components/eval-studio/evalStudioApi';
import { formatScenarioRunExecutingMessage } from '@/components/eval-studio/scenarioRunTrackingStorage';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ScenarioRunChatPreviewDialog = lazy(() =>
  import('@/components/eval-studio/ScenarioRunChatPreviewDialog').then((mod) => ({
    default: mod.ScenarioRunChatPreviewDialog,
  }))
);

interface ScenarioRunStatusPopupProps {
  run?: ScenarioRunDetail;
  pending: boolean;
  onDismiss: () => void;
  scenarioName?: string;
  versionLabel?: string;
  isDraft?: boolean;
  embedded?: boolean;
}

/**
 * Bottom-right progress popup for a single scenario run, styled like the app's
 * toasts (mirrors AgentVersionGenerationPopups). Stays in its loading state
 * while the run is queued/running and only becomes dismissible (X) once the run
 * reaches a terminal status.
 */
export function ScenarioRunStatusPopup({
  run,
  pending,
  onDismiss,
  scenarioName,
  versionLabel,
  isDraft,
  embedded = false,
}: ScenarioRunStatusPopupProps) {
  const [flowOpen, setFlowOpen] = useState(false);
  const [chatPreviewOpen, setChatPreviewOpen] = useState(false);
  const status = run?.status;
  const isLoading = pending || status === 'queued' || status === 'running' || !status;
  const isPassed = status === 'passed';
  const isSoftPass = status === 'soft_pass';
  const isFailed = status === 'failed' || status === 'error';
  const isSkipped = status === 'skipped' || status === 'cancelled';
  const showFlowChart = Boolean(run && isTerminalScenarioRunStatus(run.status));

  const asserts = (run?.step_results || []).filter((result) => result.kind === 'assert');
  const counts = asserts.reduce<Record<string, number>>((acc, result) => {
    const key = String(result.status ?? 'unknown');
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const countsLabel = Object.entries(counts)
    .map(([key, value]) => `${value} ${key}`)
    .join(' · ');

  const title = isLoading
    ? 'Running scenario'
    : isPassed
      ? 'Scenario passed'
      : isSoftPass
        ? 'Scenario soft pass'
        : isFailed
          ? 'Scenario failed'
          : 'Scenario run finished';

  const resolvedVersionLabel = run?.scenario_version_label
    ? `v${run.scenario_version_label}`
    : (versionLabel ?? 'selected version');
  const resolvedIsDraft = isDraft ?? false;
  const resolvedScenarioName = run?.scenario_name ?? scenarioName;

  const description = isLoading
    ? formatScenarioRunExecutingMessage(resolvedVersionLabel, resolvedIsDraft, resolvedScenarioName)
    : run?.error_message
      ? run.error_message
      : countsLabel || (run?.duration_ms != null ? formatCompactDuration(run.duration_ms) : 'Run complete.');

  const card = (
    <>
      <div
        data-testid="scenario-run-status-popup"
        data-status={status ?? 'pending'}
        className={cn(
          'relative flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-lg transition-colors dark:bg-gray-900',
          isFailed
            ? 'border-red-300 dark:border-red-800'
            : isPassed
              ? 'border-emerald-300 dark:border-emerald-800'
              : isSoftPass
                ? 'border-orange-300 dark:border-orange-800'
                : 'border-gray-200 dark:border-gray-700'
        )}
      >
        <div className="mt-0.5 flex-shrink-0">
          {isLoading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
          {isPassed && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
          {isSoftPass && <AlertCircle className="h-5 w-5 text-orange-500" />}
          {isFailed && <AlertCircle className="h-5 w-5 text-red-500" />}
          {isSkipped && <MinusCircle className="h-5 w-5 text-gray-400" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isLoading && <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{description}</p>
          {showFlowChart ? (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setChatPreviewOpen(true)}>
                <MessageSquare className="h-4 w-4" />
                Chat Preview
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setFlowOpen(true)}>
                <GitBranch className="h-4 w-4" />
                Flow Chart
              </Button>
            </div>
          ) : null}
        </div>

        {!isLoading && (
          <button
            aria-label="Dismiss"
            onClick={onDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {run && chatPreviewOpen ? (
        <Suspense fallback={null}>
          <ScenarioRunChatPreviewDialog run={run} open={chatPreviewOpen} onOpenChange={setChatPreviewOpen} />
        </Suspense>
      ) : null}
      {run ? <ScenarioRunFlowDialog run={run} open={flowOpen} onOpenChange={setFlowOpen} /> : null}
    </>
  );

  if (embedded) {
    return card;
  }

  return <div className="fixed bottom-4 right-4 z-[110] w-[360px] max-w-[calc(100vw-2rem)]">{card}</div>;
}
